import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import type { Category, OperationalPoint } from '../store/useStore';

// ─── Utility: group & sort points by category ──────────────────────────────

export interface CategoryGroup {
  category: Category;
  points: OperationalPoint[];
}

export interface GroupingOptions {
  /** The effective category ID of the current user (manager's own category) */
  effectiveCategoryId?: string;
  /** Whether the current user is an artist-category manager */
  isArtistManager?: boolean;
}

export function groupPointsByCategory(
  points: OperationalPoint[],
  categories: Category[],
  opts: GroupingOptions = {}
): CategoryGroup[] {
  const { effectiveCategoryId, isArtistManager } = opts;
  const catMap = new Map(categories.map(c => [c.id, c]));
  const groups = new Map<string, OperationalPoint[]>();
  const mealPoints: OperationalPoint[] = [];
  const uncategorized: OperationalPoint[] = [];

  for (const point of points) {
    // Extract meal points into their own group
    if (point.isMealPoint) {
      mealPoints.push(point);
      continue;
    }
    if (point.categoryId && catMap.has(point.categoryId)) {
      const list = groups.get(point.categoryId);
      if (list) list.push(point);
      else groups.set(point.categoryId, [point]);
    } else {
      uncategorized.push(point);
    }
  }

  // Sort points within each group alphabetically
  for (const pts of groups.values()) {
    pts.sort((a, b) => a.name.localeCompare(b.name));
  }
  mealPoints.sort((a, b) => a.name.localeCompare(b.name));
  uncategorized.sort((a, b) => a.name.localeCompare(b.name));

  // Build entries from regular category groups
  const entries: CategoryGroup[] = Array.from(groups.entries())
    .map(([catId, pts]) => ({ category: catMap.get(catId)!, points: pts }));

  // ── Ordering logic ────────────────────────────────────────────────────
  // Group types for ranking:
  //   artist, own, meal, general, public, other
  //
  // Artist manager:  artist → own → meal → general → public → other
  // Everyone else:   own → meal → artist → general → public → other

  const getGroupType = (entry: CategoryGroup): string => {
    const c = entry.category;
    if (c.isArtistCategory) return 'artist';
    if (c.id === effectiveCategoryId) return 'own';
    if (c.isGeneral) return 'general';
    if (c.isPublic) return 'public';
    return 'other';
  };

  // Assign rank to each group type based on role
  const typeRank: Record<string, number> = isArtistManager
    ? { artist: 0, own: 1, meal: 2, general: 3, public: 4, other: 5 }
    : { own: 0, meal: 1, artist: 2, general: 3, public: 4, other: 5 };

  entries.sort((a, b) => {
    const ta = getGroupType(a);
    const tb = getGroupType(b);
    const ra = typeRank[ta] ?? 5;
    const rb = typeRank[tb] ?? 5;
    if (ra !== rb) return ra - rb;
    // Within same type, sort by category name
    return a.category.name.localeCompare(b.category.name);
  });

  // ── Insert synthetic groups at their ranked position ──────────────────

  // Repas group
  if (mealPoints.length > 0) {
    const mealGroup: CategoryGroup = {
      category: { id: '__meal__', name: 'Repas', orgId: '' } as Category,
      points: mealPoints,
    };
    const mealRank = typeRank['meal'] ?? 1;
    // Find insertion index
    let insertIdx = entries.findIndex(e => {
      const t = getGroupType(e);
      return (typeRank[t] ?? 5) > mealRank;
    });
    if (insertIdx === -1) insertIdx = entries.length;
    entries.splice(insertIdx, 0, mealGroup);
  }

  // Unmatched points as "Autres" group at the very end
  if (uncategorized.length > 0) {
    entries.push({
      category: { id: '__uncategorized__', name: 'Autres', orgId: '' } as Category,
      points: uncategorized,
    });
  }

  return entries;
}

// ─── Drawer component ──────────────────────────────────────────────────────

interface CategoryDrawerProps {
  category: Category;
  pointCount: number;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}

export function CategoryDrawer({ category, pointCount, isOpen, onToggle, children }: CategoryDrawerProps) {
  const isArtist = category.isArtistCategory;
  const isGeneral = category.isGeneral;
  const isPublic = category.isPublic;
  const isMeal = category.id === '__meal__';
  const isOwn = category.id !== '__uncategorized__' && category.id !== '__meal__' && !isArtist && !isGeneral && !isPublic;

  const accentBorder = isMeal
    ? 'border-l-orange-500'
    : isArtist
      ? 'border-l-amber-500'
      : isGeneral
        ? 'border-l-emerald-500'
        : isPublic
          ? 'border-l-sky-500'
          : isOwn
            ? 'border-l-indigo-500'
            : 'border-l-slate-500';

  const badgeStyle = isMeal
    ? 'text-orange-400 bg-orange-500/10 border-orange-500/20'
    : isArtist
      ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
      : isGeneral
        ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20'
        : isPublic
          ? 'text-sky-400 bg-sky-500/10 border-sky-500/20'
          : isOwn
            ? 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20'
            : 'text-slate-400 bg-slate-500/10 border-slate-500/20';

  const badgeLabel = isMeal
    ? 'REPAS'
    : isArtist
      ? 'ARTISTE'
      : isGeneral
        ? 'GENERAL'
        : isPublic
          ? 'PUBLIC'
          : category.id === '__uncategorized__'
            ? 'AUTRES'
            : 'CATEGORIE';

  return (
    <div className="mb-6">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 bg-[#14161B] border border-[#2D3139] border-l-4 ${accentBorder} rounded-t-xl hover:bg-[#1A1D23] transition-colors group ${!isOpen ? 'rounded-b-xl' : ''}`}
      >
        <div className="flex items-center gap-3">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${badgeStyle}`}>
            {badgeLabel}
          </span>
          <span className="text-sm font-bold text-white group-hover:text-indigo-400 transition-colors">
            {category.name}
          </span>
          <span className="text-[11px] text-slate-400 bg-slate-800/80 px-2 py-0.5 rounded-full font-semibold">
            {pointCount}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="border border-t-0 border-[#2D3139] rounded-b-xl p-4 bg-[#0A0B0E]/40">
          {children}
        </div>
      )}
    </div>
  );
}
